import React, { useState } from 'react';
import { Search } from 'lucide-react';

// /support hero — rebuilt 2026-05-24 from the user's mockup to replace
// the legacy "We're Here to Help" hero (gradient orbs + floating icons
// + 3 stat cards). New design vocabulary matches the /how-it-works
// and /warranty heroes: full-bleed dark navy + subtle cyan grid +
// cyan eyebrow pill + headline-with-cyan-accent + big search input
// + popular-topic chip row. OpenbooQ brand cyan #07B0E9 and the
// site font stack (Poppins/Montserrat) are preserved.
//
// Pass 1 scope: structure + content + brand styling. The search input
// + popular-topic chips render but don't fire any behavior yet —
// wiring those to a knowledge-base search comes in a follow-up pass
// once we know the target.

const POPULAR_TOPICS = [
  'How pricing works',
  'Filing a warranty claim',
  'The install process',
  'Payment options',
];

const HeroSection = () => {
  const [query, setQuery] = useState('');

  return (
    <section
      className="relative overflow-hidden pt-48 sm:pt-36 lg:pt-44 pb-20 lg:pb-28"
      style={{
        backgroundImage: `url('/assets/images/support-hero.webp')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: '#0a1628',
      }}
    >
      {/* Dark navy overlay over the photo — keeps white headline,
          subhead, and chips legible. Same diagonal vocabulary as the
          /how-it-works hero. */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          background:
            'linear-gradient(135deg, rgba(10, 22, 40, 0.86) 0%, rgba(14, 30, 54, 0.80) 100%)',
        }}
      />
      {/* Cyan ambient glow center-top */}
      <div
        className="absolute pointer-events-none"
        aria-hidden="true"
        style={{
          top: '15%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '60%',
          height: '50%',
          background: 'radial-gradient(ellipse, rgba(7,176,233,0.18) 0%, transparent 70%)',
          filter: 'blur(40px)',
        }}
      />

      <div className="relative max-w-4xl mx-auto px-5 lg:px-8 text-center text-white">
        {/* Eyebrow pill — cyan, "Help Center" with green dot */}
        <div className="mb-7 flex justify-center">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.14em]"
            style={{
              background: 'rgba(7, 176, 233, 0.18)',
              border: '1px solid rgba(7, 176, 233, 0.45)',
              color: '#6cc7ff',
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: '#10B981' }}
              aria-hidden="true"
            />
            Help Center
          </div>
        </div>

        {/* Headline — "How can we help?" with "help?" in cyan */}
        <h1
          className="font-black leading-[1.05] tracking-tight mb-6"
          style={{
            fontSize: 'clamp(40px, 6.5vw, 76px)',
            fontFamily: "'Poppins', 'Montserrat', sans-serif",
            letterSpacing: '-0.025em',
          }}
        >
          How can we{' '}
          <span style={{ color: '#07B0E9' }}>help?</span>
        </h1>

        {/* Subhead */}
        <p
          className="mb-10 max-w-2xl mx-auto leading-relaxed"
          style={{
            fontSize: 'clamp(15px, 1.4vw, 18px)',
            color: 'rgba(255, 255, 255, 0.82)',
          }}
        >
          Search our knowledge base, browse common questions, or reach a real roofer. Whatever stage you're at — shopping, mid-project, or post-install — the answer's here.
        </p>

        {/* Search bar — white pill with cyan magnifying-glass icon on
            the left, text input in the middle, cyan "Search" button on
            the right. Slight shadow lifts it off the dark background. */}
        <form
          className="mx-auto mb-6 flex items-center gap-1.5 bg-white rounded-full overflow-hidden"
          style={{
            maxWidth: '760px',
            padding: '7px 7px 7px 22px',
            boxShadow: '0 18px 48px rgba(7, 176, 233, 0.18), 0 4px 14px rgba(0, 0, 0, 0.10)',
          }}
          onSubmit={(e) => e.preventDefault()}
          role="search"
          aria-label="Search the help center"
        >
          <Search
            size={20}
            strokeWidth={2.4}
            style={{ color: '#07B0E9' }}
            className="flex-shrink-0"
            aria-hidden="true"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search articles, guides, and answers..."
            className="flex-1 min-w-0 bg-transparent outline-none border-none text-[15px] lg:text-base text-gray-900 placeholder-gray-400 px-3 py-3"
            aria-label="Search articles, guides, and answers"
          />
          <button
            type="submit"
            className="flex-shrink-0 font-bold text-white px-6 sm:px-7 py-3 rounded-full transition-transform duration-200 hover:-translate-y-0.5"
            style={{
              background: '#07B0E9',
              fontSize: '15px',
              boxShadow: '0 8px 20px rgba(7, 176, 233, 0.35)',
            }}
          >
            Search
          </button>
        </form>

        {/* Popular topics row — "Popular:" label + 4 chip-style pills */}
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
          <span
            className="text-sm font-semibold mr-1"
            style={{ color: 'rgba(255, 255, 255, 0.65)' }}
          >
            Popular:
          </span>
          {POPULAR_TOPICS.map((topic) => (
            <button
              key={topic}
              type="button"
              onClick={() => setQuery(topic)}
              className="px-4 py-2 rounded-full text-sm font-medium text-white/85 hover:text-white transition-colors duration-200"
              style={{
                background: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
              }}
            >
              {topic}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
